import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useParams, Link } from 'react-router-dom';
import useIsMounted from '../../hooks/useIsMounted.mjs';

/**
 * Breadcrumbs - Params-first navigation breadcrumbs.
 * 
 * STRUCTURE (which crumbs to show) is derived from useParams() — always
 * synchronously current on every navigation.
 * 
 * LABELS (display text) are looked up from Redux, but ONLY when the slug
 * matches the current params (prevents stale cross-reads). Falls back to
 * slugToLabel() for instant rendering.
 */

const slugToLabel = (slug) =>
    slug ? slug.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') : '';

/**
 * Safe wrapper for useParams — returns {} during SSR (no Router context).
 */
const useSafeParams = () => {
    try {
        return useParams();
    } catch {
        return {};
    }
};

const Breadcrumbs = () => {
    const isClient = useIsMounted();
    // Route params — PRIMARY source of truth for breadcrumb structure (client-side)
    const {
        categorySlug: paramCategory,
        '*': topicPath,
    } = useSafeParams();

    // Parse splat into segments: ['ncaa-basketball', 'duke'] for /sports/ncaa-basketball/duke
    const topicSegments = (topicPath || '').split('/').filter(Boolean);
    const paramTopic = topicSegments[topicSegments.length - 1] || undefined;
    const ancestorSlugs = topicSegments.slice(0, -1);

    // Redux — used for label lookups AND as SSR fallback for slugs
    const currentScope = useSelector((state) => state.unifiedArticles?.currentScope);
    const pageMeta = useSelector((state) => state.pageMetadata?.metadata);
    const currentTopic = useSelector((state) => state.currentTopic);

    // On SSR, useParams() returns {} (no Router context).
    // Fall back to currentScope slugs from Redux (set by SSR controller).
    // On client, ALWAYS use params — they're synchronously current.
    const isSSR = !paramCategory && !paramTopic && typeof window === 'undefined';

    const category = paramCategory || (isSSR ? currentScope?.category : undefined);
    const topic = paramTopic || (isSSR ? currentScope?.topic : undefined);
    // For SSR fallback, get ancestors from Redux
    const ssrAncestors = isSSR ? (currentScope?.ancestors || []) : ancestorSlugs;
    const isRootRoute = !category || (isSSR && pageMeta?.isRootRoute);

    // Build ancestor labels from Redux state (SSR controller provides them)
    const reduxAncestors = currentTopic?.ancestors || pageMeta?.ancestors || [];

    // Detect article page: during SSR, currentScope.type === 'article';
    // on client, the last topic segment is the article slug (not a topic slug)
    const isArticlePage = currentScope?.type === 'article';

    const crumbs = useMemo(() => {
        // No category = root route (/)
        if (isRootRoute) {
            return [{ label: 'Home' }];
        }

        const items = [{ label: 'Home', href: '/' }];

        // --- Category crumb ---
        // Only use Redux label if it matches the current category slug
        let categoryLabel = slugToLabel(category);
        if (pageMeta?.categoryCode === category && pageMeta?.categoryLabel) {
            categoryLabel = pageMeta.categoryLabel;
        }
        if (currentTopic?.category?.code === category && currentTopic?.category?.label) {
            categoryLabel = currentTopic.category.label;
        }

        if (topic || isArticlePage) {
            // We're on a topic or article page — category is a link
            items.push({ label: categoryLabel, href: `/${category}` });
        } else {
            // We're on the category page itself — category is current
            items.push({ label: categoryLabel });
            return items;
        }

        // --- Ancestor crumbs (N-level) ---
        ssrAncestors.forEach((slug, i) => {
            let label = slugToLabel(slug);
            // Look up label from Redux ancestors array if available
            const reduxAncestor = reduxAncestors.find(a => a.slug === slug);
            if (reduxAncestor?.label) {
                label = reduxAncestor.label;
            }
            // On article pages, the topic from SSR becomes an ancestor on the client.
            // Fall back to currentTopic / pageMeta for the correct DB label to avoid
            // hydration mismatch (slugToLabel produces naive title-case like "Ucf" vs "UCF").
            if (label === slugToLabel(slug)) {
                if (currentTopic?.slug === slug && currentTopic?.label) {
                    label = currentTopic.label;
                } else if (pageMeta?.topicCode === slug && pageMeta?.topicLabel) {
                    label = pageMeta.topicLabel;
                } else if (currentScope?.topic === slug && currentScope?.topicLabel) {
                    label = currentScope.topicLabel;
                }
            }
            const href = `/${category}/${ssrAncestors.slice(0, i + 1).join('/')}`;
            items.push({ label, href });
        });

        // --- Current topic crumb ---
        // On article pages during SSR, topic is set from currentScope but
        // should be rendered as a link (not current page). On the client,
        // the topic is already in ancestorSlugs, so we only add it here for SSR.
        if (topic && isSSR && isArticlePage) {
            // SSR article page: topic is the parent, render as link
            let topicLabel = slugToLabel(topic);
            if (currentTopic?.slug === topic && currentTopic?.label) {
                topicLabel = currentTopic.label;
            }
            const topicHref = `/${category}/${[...ssrAncestors, topic].join('/')}`;
            items.push({ label: topicLabel, href: topicHref });
        } else if (topic && !isArticlePage) {
            // Topic page: topic is the current page (no link)
            let topicLabel = slugToLabel(topic);
            if (currentTopic?.slug === topic && currentTopic?.label) {
                topicLabel = currentTopic.label;
            }
            if (pageMeta?.topicCode === topic && pageMeta?.topicLabel) {
                topicLabel = pageMeta.topicLabel;
            }
            items.push({ label: topicLabel });
        }

        return items;
    }, [category, topic, ssrAncestors, isRootRoute, isArticlePage, isSSR, pageMeta, currentTopic, currentScope, reduxAncestors]);

    if (!crumbs || crumbs.length === 0) return null;

    return (
        <nav className="breadcrumbs" aria-label="Breadcrumb">
            <ol className="breadcrumbs__list">
                {crumbs.map((crumb, index) => {
                    const isLast = index === crumbs.length - 1;

                    return (
                        <li key={crumb.href || crumb.label} className="breadcrumbs__item">
                            {index > 0 && (
                                <span className="breadcrumbs__separator" aria-hidden="true">
                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                        <path d="M6 3L11 8L6 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </span>
                            )}
                            {isLast || !crumb.href ? (
                                <span className="breadcrumbs__current" aria-current="page">
                                    {crumb.label}
                                </span>
                            ) : isClient ? (
                                <Link to={crumb.href} className="breadcrumbs__link">
                                    {crumb.label}
                                </Link>
                            ) : (
                                <a href={crumb.href} className="breadcrumbs__link">
                                    {crumb.label}
                                </a>
                            )}
                        </li>
                    );
                })}
            </ol>
        </nav>
    );
};

export default Breadcrumbs;

